// Common types for the application

export interface HealthStatus {
  status: string;
  timestamp: string;
}

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Authentication types
export interface LoginFormData {
  username: string;
  password: string;
  schoolCode?: string;
}

export interface User {
  id: string;
  cmsUserId: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  school?: string;
  position?: string;
  roles: string[];
  notes?: string;
  dateCreated: string;
  lastLogin?: string;
  telegramEnabled?: boolean;
  telegramChatId?: string;
  changePasswordRequired?: boolean; // Flag to track if user needs to change password (first-time login)
  schoolCode?: string | null;
  schoolName?: string | null;
  isSuperAdmin?: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Book and Category types
export interface Category {
  id: string;
  name: string;
  description?: string;
  dateCreated: string;
  dateUpdated?: string;
  isDeleted: boolean;
  type?: 'book' | 'resource'; // Phân loại: 'book' cho sách vật lý, 'resource' cho sách số
  bookCount?: number; // Số lượng loại sách trong danh mục này
  resourceCount?: number; // Số lượng tài nguyên trong chủ đề này (cho resource topics)
}

export interface Subject {
  id: string;
  name: string;
  description?: string;
  dateCreated: string;
  isDeleted: boolean;
  lessonPlanCount?: number; // Số lượng giáo án trong môn học này
}

export interface Book {
  id: string;
  title: string;
  author: string;
  isbn?: string;
  publisher?: string;
  publishYear?: number;
  subjects: string[];
  categoryId?: string; // Backward compatibility: single category
  category?: Category; // Backward compatibility: single category
  categoryIds?: string[]; // New: array of category IDs
  categories?: Category[]; // New: array of categories
  summary?: string;
  notes?: string;
  totalCopies: number;
  availableCopies: number;
  dateCreated: string;
  dateUpdated?: string;
  isDeleted: boolean;
}

export interface BookFormData {
  title: string;
  author: string;
  isbn?: string;
  publisher?: string;
  publishYear?: number;
  subjects: string[];
  categoryId?: string; // Backward compatibility: single category
  categoryIds?: string[]; // New: array of category IDs
  summary?: string;
  notes?: string;
  totalCopies: number;
  availableCopies: number;
}

export interface BookListParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  categoryId?: string;
  availabilityFilter?: string; // 'available' or 'loaned'
}

// Resource types
export interface Resource {
  id: string;
  title: string;
  description?: string;
  tags: string[];
  categories?: Category[]; // Array of categories
  category?: Category; // Backward compatibility: single category
  categoryId?: string; // Backward compatibility: single category ID
  categoryIds?: string[]; // Array of category IDs
  uploadedByUserId: string;
  uploadedByUser?: {
    id: string;
    fullName: string;
    email: string;
  };
  uploadDate: string;
  filePath: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  viewCount: number;
  downloadCount: number;
  aiStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  dateCreated: string;
  dateUpdated?: string;
  isDeleted: boolean;
}

export interface ResourceListParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  fileType?: string;
  tags?: string;
  tag?: string; // Filter by single tag
  categoryId?: string; // Filter by category
  excludeCategoryId?: string; // Exclude resources in this category (server-side filter for pagination)
  startDate?: string;
  endDate?: string;
}

export interface ResourceListResponse {
  resources: Resource[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Lesson Plan types
export interface LessonPlan {
  id: string;
  title: string;
  subject?: string | null; // Subject name (for display, from subjects table or text field)
  subjectId?: string | null; // Subject ID (UUID from subjects table)
  week?: number | null;
  submittedByUserId: string;
  submittedByUser?: {
    id: string;
    fullName: string;
    email: string;
  };
  submissionDate: string;
  status: 'Pending' | 'PendingSubmit' | 'UnderReview' | 'NeedsRevision' | 'Approved' | 'Draft';
  filePath: string;
  fileName: string;
  approvedByUserId?: string | null;
  approvedByUser?: {
    id: string;
    fullName: string;
    email: string;
  };
  approvalDate?: string | null;
  revisionNotes?: string | null;
  dateCreated: string;
  dateModified?: string | null;
  isDeleted: boolean;
  folderId?: string | null;
  folder?: {
    id: string;
    name: string | null;
  } | null;
  content?: string | null;
  is_digital?: boolean;
}

export interface LessonPlanFormData {
  title: string;
  subject?: string; // Deprecated: use subjectId instead
  subjectId?: string | null; // Subject ID from subjects table
  week?: number;
  file?: File;
  folderId?: string | null;
  submitImmediately?: boolean;
  content?: string | null;
  is_digital?: boolean;
}

export interface LessonPlanRevisionFormData {
  file: File;
  revisionNotes?: string;
}

export interface LessonPlanListParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  status?: string;
  excludeStatus?: string;
  myPlans?: boolean;
  submittedByUserId?: string;
  folderId?: string;
  subjectId?: string;
  week?: number;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'title' | 'submissiondate'; // Sắp xếp theo tên hoặc thời gian upload
}

export interface LessonPlanListResponse {
  lessonPlans: LessonPlan[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface LessonPlanStatus {
  value: string;
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export const LESSON_PLAN_STATUSES: LessonPlanStatus[] = [
  { value: 'Draft', label: 'Bản nháp', color: 'default' },
  { value: 'PendingSubmit', label: 'Chờ nộp', color: 'secondary' },
  { value: 'Pending', label: 'Chờ duyệt', color: 'warning' },
  { value: 'UnderReview', label: 'Đang xem xét', color: 'info' },
  { value: 'NeedsRevision', label: 'Cần chỉnh sửa', color: 'error' },
  { value: 'Approved', label: 'Đã duyệt', color: 'success' }
];

// Lesson Plan Folder types
export interface LessonPlanFolder {
  id: string;
  name: string;
  ownerUserId: string | null;
  ownerName: string | null;
  parentFolderId: string | null;
  isSystem: boolean;
  dateCreated: string;
}

export interface LessonPlanFolderFormData {
  name: string;
  parentFolderId?: string | null;
}

// Export notification types
export * from './notification';
export type {
  UserNotificationPreferences,
  NotificationTypeEnum,
  NotificationChannel,
  NotificationPreferencesService,
  NotificationPreferencesFormData
} from './notificationPreferences';
export {
  DEFAULT_NOTIFICATION_TYPES,
  PRIORITY_LEVELS,
  NOTIFICATION_TYPE_LABELS
} from './notificationPreferences';
